import * as CookieConsent from 'vanilla-cookieconsent';
import { reporter } from '~/hooks/useErrorReporter';
import type { ConsentParams, CookieCategories } from '../../constants';
import mapCookieCategoriesToGaConsent from '../mapCookieCategoriesToGaConsent';

type CookieCategoryPreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
};

// Mirrors what BE wants to receive
type ConsentDataInput = {
  id: string;
  gaConsentMode: ConsentParams;
  cookiePreferences: CookieCategoryPreferences;
  version: number;
  host: string;
};

const cookieCategoriesToCookiePreferencesObj = (
  categories: Array<CookieCategories>,
): CookieCategoryPreferences => ({
  necessary: categories.includes('necessary'),
  analytics: categories.includes('analytics'),
  marketing: categories.includes('marketing'),
  functional: categories.includes('functional'),
});

/**
 * Get the current domain host.
 * Returns the hostname from window.location.
 */
const getDomainHost = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.hostname;
};

const storeConsent = async (cookie: CookieConsent.CookieValue) => {
  try {
    const consentStorage = process.env.COOKIE_CONSENT_STORAGE_URL;

    if (!consentStorage) {
      throw new Error(`consentStorage is not defined`);
    }

    const gaConsentMode = mapCookieCategoriesToGaConsent(
      cookie.categories as Array<CookieCategories>,
    );

    const cookiePreferences = cookieCategoriesToCookiePreferencesObj(
      cookie.categories as Array<CookieCategories>,
    );

    const host = getDomainHost();

    const data: ConsentDataInput = {
      gaConsentMode,
      id: cookie.consentId,
      host,
      version: 0,
      cookiePreferences,
    };

    const response = await fetch(consentStorage, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error);
    }

    return await response.json();
  } catch (error) {
    reporter.captureException(
      new Error(`Error storing consent: ${JSON.stringify(error, null, 2)}`),
      'error',
    );
  }
};

export default storeConsent;
